import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addDays, date, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { PageHeader, SectionCard, StatCard, StatGrid } from '../../ui/primitives';
import CallNoticePreview from './CallNoticePreview';

const STEPS = ['Call details', 'Review notices', 'Submit for approval'];

export default function CallWizardPage() {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const commitmentFunds = scope.funds.filter((fund) => fund.basis === 'COMMITMENT');
  const [step, setStep] = useState(0);
  const [fundId, setFundId] = useState(params.get('fund') ?? commitmentFunds[0]?.id ?? '');
  const [callPct, setCallPct] = useState('10');
  const [dueDate, setDueDate] = useState(addDays(DEMO_TODAY, 21));
  const [purpose, setPurpose] = useState('Equity for the Riverbend townhouse JV (land settlement)');
  const fund = scope.fundById.get(fundId);
  const unitClass = scope.classes.find((item) => item.fundId === fundId);
  const pct = Number(callPct) || 0;

  const lines = useMemo(
    () =>
      scope.holdings
        .filter((holding) => holding.classId === unitClass?.id && (holding.commitment ?? 0) > 0)
        .map((holding) => ({
          holderId: holding.holderId,
          commitment: holding.commitment ?? 0,
          paidToDate: holding.costBase,
          amount: Math.min(
            Math.round(((holding.commitment ?? 0) * pct) / 100),
            holding.uncalled ?? 0,
          ),
          uncalledAfter: Math.max(
            0,
            (holding.uncalled ?? 0) - Math.round(((holding.commitment ?? 0) * pct) / 100),
          ),
        }))
        .filter((line) => line.amount > 0),
    [scope.holdings, unitClass, pct],
  );
  const total = sumBy(lines, (line) => line.amount);
  const uncalledNow = sumBy(
    scope.holdings.filter((holding) => holding.classId === unitClass?.id),
    (holding) => holding.uncalled ?? 0,
  );

  if (!commitmentFunds.length) {
    return <Alert severity="info">No commitment funds for this tenant.</Alert>;
  }

  return (
    <>
      <PageHeader
        title="New capital call"
        subtitle="Calculated from each holder's commitment and uncalled balance on the register."
      />
      <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {step === 0 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField
              select
              label="Fund"
              value={fundId}
              onChange={(event) => setFundId(event.target.value)}
            >
              {commitmentFunds.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.code} · {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Call (% of commitment)"
              type="number"
              value={callPct}
              onChange={(event) => setCallPct(event.target.value)}
              inputProps={{ min: 1, max: 100 }}
              helperText={`Uncalled now: ${money(uncalledNow)}`}
            />
            <TextField
              label="Due date"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Trust deed minimum notice: 10 business days"
            />
            <TextField
              label="Issue date"
              value={date(DEMO_TODAY)}
              disabled
              helperText="Notices go out on approval"
            />
            <TextField
              label="Purpose (shown on the notice)"
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
              multiline
              minRows={2}
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              disabled={!pct || !purpose.trim() || lines.length === 0}
              onClick={() => setStep(1)}
            >
              Calculate notices
            </Button>
          </Box>
        </Paper>
      )}
      {step === 1 && (
        <>
          <StatGrid>
            <StatCard label="Notices" value={lines.length} />
            <StatCard label="Total called" value={money(total)} />
            <StatCard label="Uncalled after this call" value={money(uncalledNow - total)} />
            <StatCard label="Due" value={date(dueDate)} />
          </StatGrid>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' } }}>
            <SectionCard title="Per-holder amounts" noPadding>
              <DataTable
                rows={lines}
                rowKey={(row) => row.holderId}
                dense
                pageSize={12}
                defaultSort={{ key: 'amount', direction: 'desc' }}
                columns={[
                  {
                    key: 'holder',
                    label: 'Holder',
                    sortValue: (row) => scope.holderById.get(row.holderId)?.name ?? '',
                    render: (row) => scope.holderById.get(row.holderId)?.name,
                  },
                  {
                    key: 'commitment',
                    label: 'Commitment',
                    align: 'right',
                    sortValue: (row) => row.commitment,
                    render: (row) => money(row.commitment),
                  },
                  {
                    key: 'paid',
                    label: 'Paid to date',
                    align: 'right',
                    render: (row) => money(row.paidToDate),
                  },
                  {
                    key: 'amount',
                    label: 'This call',
                    align: 'right',
                    sortValue: (row) => row.amount,
                    render: (row) => <strong>{money(row.amount)}</strong>,
                  },
                  {
                    key: 'after',
                    label: 'Uncalled after',
                    align: 'right',
                    render: (row) => money(row.uncalledAfter),
                  },
                ]}
              />
            </SectionCard>
            {fund && lines[0] && (
              <CallNoticePreview
                fund={fund}
                holderName={scope.holderById.get(lines[0].holderId)?.name ?? ''}
                amount={lines[0].amount}
                callPct={pct}
                dueDate={dueDate}
                purpose={purpose}
                reference="78XXXXX"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button variant="contained" onClick={() => setStep(2)}>
              Continue
            </Button>
          </Box>
        </>
      )}
      {step === 2 && fund && unitClass && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
          <Typography variant="h6">Ready to submit</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {fund.code} call of {pct}% · {lines.length} notices · {money(total)} due {date(dueDate)}
            . A second person must approve before notices are released to the investor app and
            email, and tasks are created for each holder.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button
              variant="contained"
              onClick={() => {
                dispatch({
                  type: 'CALL_CREATE',
                  fundId: fund.id,
                  classId: unitClass.id,
                  callPct: pct,
                  dueDate,
                  purpose,
                });
                toast('Capital call submitted for approval');
                navigate('/manager/approvals');
              }}
            >
              Submit for approval
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
}
